/**
 * Central frontend mirror of the backend `PermissionList.cs`.
 * Source of truth: yasc-enterprise-backend/CpCore/Modules/Core/PermissionList.cs
 *
 * Naming convention: module:resource:action[:scope]
 * Scope values:
 *   own  - the authenticated user / own company / own entity
 *   all  - other users, tenant-wide or administrative access
 *
 * Property names mirror the C# `PermissionList` field names so a value can
 * always be traced back to its backend definition.
 */
export const PermissionList = {
    // General
    GeneralNotifications: 'general:notifications:receive',
    CreateBugTicket: 'general:ticket:create',
    MiscGetDevelopers: 'general:developers:search',

    // Module gates (side navigation visibility only)
    ModuleAdministration: 'module:administration',
    ModuleCP: 'module:cp',
    ModuleCRM: 'module:crm',
    ModuleRM: 'module:rm',
    ModuleFibu: 'module:fibu',
    ModuleFinances: 'module:finances',
    ModuleContracts: 'module:contracts',
    ModuleInventory: 'module:inventory',

    // Administration
    AdminEnvSearch: 'admin:environment:search',
    AdminServerLogSearch: 'admin:diagnostic_logs:search',
    AdminResourceManagerViewEmployees: 'admin:resource_manager_employees:search',
    AdminEmailClientAdd: 'admin:email_client:create',
    AdminEmailClientSearch: 'admin:email_client:search',
    AdminEmailClientUpdate: 'admin:email_client:update',
    AdminEmailClientTestConnection: 'admin:email_client:test_connection',
    AdminEmailClientRemove: 'admin:email_client:delete',
    AdminVariableUpdate: 'admin:access_variables:update',
    AdminVariableSearch: 'admin:access_variables:search',
    AdminVariableDelete: 'admin:access_variables:reset',
    AdminDatabaseBackup: 'admin:database:backup',
    AdminDatabaseRestore: 'admin:database:restore',
    AdminDatabaseSearch: 'admin:database:search',
    AdminRoleSearch: 'admin:roles:search',
    AdminRoleAssign: 'admin:roles:assign',
    AdminRoleRevoke: 'admin:roles:revoke',
    AdminPermissionSearch: 'admin:permissions:search',
    AdminPermissionAssign: 'admin:permissions:assign',
    AdminPermissionRevoke: 'admin:permissions:revoke',
    AdminRolePermissionsReset: 'admin:permissions:reset',
    AdminAccountOnboard: 'admin:accounts:onboard:all',
    AdminAccountEmployment: 'admin:accounts:employment:all',
    AdminAccountSetPassword: 'admin:accounts:set_password:all',
    AdminAccountLockout: 'admin:accounts:lockout:all',
    AdminAccountUnlock: 'admin:accounts:unlock:all',

    // Account
    AccountRegister: 'core:account:register:all',
    AccountDetails: 'core:account:details:own',
    AccountDetailsId: 'core:account:details:all',
    AccountUpdate: 'core:account:update:own',
    AccountUpdateId: 'core:account:update:all',
    AccountDelete: 'core:account:delete:own',
    AccountDeleteId: 'core:account:delete:all',
    AccountSearch: 'core:account:search:all',
    AccountAssignRole: 'core:account:assign_role:all',
    AccountConfirmMail: 'core:account:confirm_mail:own',
    AccountChangePassword: 'core:account:change_password:own',
    AccountChangePasswordId: 'core:account:change_password:all',
    AccountPermissions: 'core:account:permissions:own',
    AccountPermissionsId: 'core:account:permissions:all',
    AuthLogin: 'core:auth:login',

    // Company
    CompanyOnboard: 'core:company:onboard',
    CompanyAdd: 'core:company:create:all',
    CompanyDetails: 'core:company:details:own',
    CompanyAddressCreate: 'core:company_address:create',
    CompanyUpdate: 'core:company:update:all',
    CompanyDelete: 'core:company:delete:all',
    CompanySearch: 'core:company:search:all',
    CompanyProducts: 'core:company_products:search:own',
    CompanyBills: 'core:company_bills:search:own',
    CompanyDepartments: 'core:company_departments:search:own',
    CompanyBankAccounts: 'core:company_bank_accounts:search:own',
    CompanyEmployees: 'core:company_employees:search:own',
    CompanyPayments: 'core:company_payments:search:own',

    // File
    FileGet: 'core:file:download:own',
    FileUpload: 'core:file:upload:own',
    FileRemoveOwn: 'core:file:delete:own',
    FileRemoveOther: 'core:file:delete:all',

    // Customer portal
    LicenseAssign: 'cp:license:assign:all',
    LicenseRevoke: 'cp:license:revoke:all',
    LicenseOrder: 'cp:license:order:all',
    LicenseCancel: 'cp:license:cancel:all',
    NewsSearch: 'cp:news:search',
    NewsAdd: 'cp:news:create',
    NewsUpdate: 'cp:news:update',
    NewsDelete: 'cp:news:delete',
    ProductCreate: 'cp:product:create',
    ProductUpdate: 'cp:product:update',
    ProductDetails: 'cp:product:details',
    ProductActivate: 'cp:product:activate:own',
    ProductSearch: 'cp:product:search',
    ProductRelease: 'cp:product:release',
    ProjectCreate: 'cp:project:create',
    ProjectUpdate: 'cp:project:update',
    ProjectDetails: 'cp:project:details',
    ProjectStatistics: 'cp:project:stats',
    ProjectInvoices: 'cp:project_invoices:search',
    ProjectOffers: 'cp:project_offers:search',
    ProjectSearch: 'cp:project:search',
    ProjectDelete: 'cp:project:delete',
    ProjectMerge: 'cp:project:merge',
    TaskCreate: 'cp:task:create',
    TaskUpdate: 'cp:task:update',
    TaskDelete: 'cp:task:delete',
    TaskSearch: 'cp:task:search',
    TaskReferenceUpdate: 'cp:task_reference:update',
    TaskReferenceDelete: 'cp:task_reference:delete',
    TaskEffortCreate: 'cp:task_effort:create:own',
    TaskEffortUpdate: 'cp:task_effort:update:own',
    TaskEffortDelete: 'cp:task_effort:delete:own',
    InvoiceCreate: 'cp:invoice:create',
    InvoiceSearch: 'cp:invoice:search',
    InvoiceUpdate: 'cp:invoice:update',
    InvoiceDelete: 'cp:invoice:delete',
    InvoiceSend: 'cp:invoice:send',
    OfferCreate: 'cp:offer:create',
    OfferSearch: 'cp:offer:search',
    OfferUpdate: 'cp:offer:update',
    OfferDelete: 'cp:offer:delete',
    OfferSend: 'cp:offer:send',

    // CRM
    NotesCreate: 'crm:notes:create',
    NotesDetails: 'crm:notes:details',
    NotesUpdate: 'crm:notes:update',
    NotesSearch: 'crm:notes:search',
    NotesDelete: 'crm:notes:delete',
    WorkflowCreate: 'crm:workflow:create',
    WorkflowUpdate: 'crm:workflow:update',
    WorkflowSearch: 'crm:workflow:search',
    WorkflowDelete: 'crm:workflow:delete',
    ContactPersonCreate: 'crm:contact:create',
    ContactPersonUpdate: 'crm:contact:update',
    ContactPersonDelete: 'crm:contact:delete',
    ContactPersonSearch: 'crm:contact:search',
    TrackerAdvance: 'crm:tracker:advance',
    TrackerDeactivate: 'crm:tracker:deactivate',

    // Fibu (bookkeeping)
    FibuCategoriesSearch: 'fibu:categories:search',
    FibuAccountsSearch: 'fibu:accounts:search',
    FibuAccountsUpdate: 'fibu:accounts:update',
    FibuAccountsAdd: 'fibu:accounts:create',
    FibuAccountsDelete: 'fibu:accounts:delete',
    FibuBookingCreate: 'fibu:booking:create',
    FibuBookingDelete: 'fibu:booking:delete',
    FibuBookingUpdate: 'fibu:booking:update',
    FibuBookingSearch: 'fibu:booking:search',
    FibuBookingBookingJournal: 'fibu:booking:book',
    FibuBookingImport: 'fibu:booking:import',
    FibuBookingExport: 'fibu:booking:export',

    // Finances
    FibuBankSearch: 'finances:bank:search',
    FibuBankTransactionsSearch: 'finances:transactions:search',
    FibuBankTxAliasSearch: 'finances:bank_aliases:search',
    FibuBankTxAliasCreate: 'finances:bank_aliases:create',
    FibuBankConnectionsSearch: 'finances:bank_connections:search',
    FibuBankConnectionsAdd: 'finances:bank_connections:create',
    FibuBankConnectionRemove: 'finances:bank_connections:delete',
    FibuBankAccountsSearch: 'finances:bank_accounts:search',
    BankAccountDelete: 'finances:bank_account:delete',

    // Resource manager
    ResourceManagerAppointments: 'rm:appointments:search:own',
    ResourceManagerAppointmentCreate: 'rm:appointments:create:own',
    ResourceManagerTimeEntries: 'rm:time_entries:search:own',
    ResourceManagerTimeEntriesCreate: 'rm:time_entries:create:own',
    ResourceManagerApproveOffDays: 'rm:time_entries:approve:all',
    CalendarRead: 'rm:calendar:read:own',
    CalendarWrite: 'rm:calendar:write:own',
    CalendarCancelOwn: 'rm:calendar:cancel:own',
    CalendarInvite: 'rm:calendar:invite:own',
    CalendarDecideOwn: 'rm:calendar:decide:own',
    CalendarApprove: 'rm:calendar:approve:all',
    CalendarSearch: 'rm:calendar:search:own',
    CalendarExportProject: 'rm:calendar:export:project',
    CalendarSearchAll: 'rm:calendar:search:all',
    CalendarAdmin: 'rm:calendar:admin:all',

    // Microsoft
    TeamsPresence: 'rm:microsoft:teams:presence',

    // Contracts
    ContractingList: 'contracts:license_assignment:list',
    ContractingSearch: 'contracts:license_assignment:search',
    ContractingDetails: 'contracts:license_assignment:details',
    ContractingCreate: 'contracts:license_assignment:create',
    ContractingUpdate: 'contracts:license_assignment:update',
    ContractingDelete: 'contracts:license_assignment:delete',
    ContractingDeviceDataList: 'contracts:device_data:list',
    ContractingDeviceDataSearch: 'contracts:device_data:search',
    ContractingDeviceDataDetails: 'contracts:device_data:details',
    ContractingDeviceDataCreate: 'contracts:device_data:create',
    ContractingDeviceDataUpdate: 'contracts:device_data:update',
    ContractingDeviceDataDelete: 'contracts:device_data:delete',
    ContractingLicenseDataList: 'contracts:license_data:list',
    ContractingLicenseDataSearch: 'contracts:license_data:search',
    ContractingLicenseDataDetails: 'contracts:license_data:details',
    ContractingLicenseDataCreate: 'contracts:license_data:create',
    ContractingLicenseDataUpdate: 'contracts:license_data:update',
    ContractingLicenseDataDelete: 'contracts:license_data:delete',

    // Inventory
    InventoryArticleList: 'inventory:article:list',
    InventoryArticleSearch: 'inventory:article:search',
    InventoryArticleDetails: 'inventory:article:details',
    InventoryArticleCreate: 'inventory:article:create',
    InventoryArticleUpdate: 'inventory:article:update',
    InventoryArticleDelete: 'inventory:article:delete',
    InventoryComputerList: 'inventory:computer:list',
    InventoryComputerSearch: 'inventory:computer:search',
    InventoryComputerDetails: 'inventory:computer:details',
    InventoryComputerCreate: 'inventory:computer:create',
    InventoryComputerUpdate: 'inventory:computer:update',
    InventoryComputerDelete: 'inventory:computer:delete',
    InventoryDisplayList: 'inventory:display:list',
    InventoryDisplaySearch: 'inventory:display:search',
    InventoryDisplayDetails: 'inventory:display:details',
    InventoryDisplayCreate: 'inventory:display:create',
    InventoryDisplayUpdate: 'inventory:display:update',
    InventoryDisplayDelete: 'inventory:display:delete',
} as const;

export type PermissionName = typeof PermissionList[keyof typeof PermissionList];
